import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

// Use relative URL for same-origin requests, or configured backend URL
const getBackendUrl = () => {
  // In production, use same origin to avoid CORS issues
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      // Try to get session_id from hash first, then from query params
      let sessionId = null;
      
      // Check hash (e.g., /dashboard#session_id=xxx)
      if (location.hash) {
        const hash = location.hash.substring(1);
        const hashParams = new URLSearchParams(hash);
        sessionId = hashParams.get('session_id');
      }
      
      // Check query params as fallback (e.g., /dashboard?session_id=xxx)
      if (!sessionId && location.search) {
        const queryParams = new URLSearchParams(location.search);
        sessionId = queryParams.get('session_id');
      }
      
      // Also check the full URL in case of edge cases
      if (!sessionId) {
        const fullUrl = window.location.href;
        const match = fullUrl.match(/session_id=([^&]+)/);
        if (match) {
          sessionId = match[1];
        }
      }

      console.log('AuthCallback: Processing session_id:', sessionId ? 'found' : 'not found');

      if (!sessionId) {
        console.error('AuthCallback: No session_id found in URL');
        toast.error('Authentication failed - no session ID');
        navigate('/login');
        return;
      }

      try {
        const backendUrl = getBackendUrl();
        console.log('AuthCallback: Sending session to backend...', backendUrl);
        const response = await fetch(`${backendUrl}/api/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('AuthCallback: Session creation failed', response.status, errorData);
          throw new Error(errorData.detail || 'Session creation failed');
        }

        const userData = await response.json();
        console.log('AuthCallback: Login successful for', userData.email);
        toast.success(`Welcome back, ${userData.name}!`);
        
        // Clear the hash/query from URL and navigate
        window.history.replaceState({}, document.title, '/dashboard');
        navigate('/dashboard', { state: { user: userData }, replace: true });
      } catch (error) {
        console.error('Auth error:', error);
        toast.error(`Authentication failed: ${error.message}`);
        navigate('/login');
      }
    };

    processSession();
  }, [navigate, location]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent mb-4"></div>
        <p className="text-muted-foreground font-mono">Authenticating...</p>
      </div>
    </div>
  );
}

export default AuthCallback;